import { Icon } from "@iconify/react/dist/iconify.js";

import { useState, useEffect } from "react";
import { fetchRoles, createUser, assignUserRole, addUserToOrganization, fetchOrganizations, fetchOrgMembers, updateUser, deleteUser, updateUserOrgRole } from "../../api/auth0Api";
import { findAccountByName, createContact } from "../../api/ticketData";
import { useAuth0 } from "../../components/auth/DevAuth0Provider";
import { useClientsData } from "../../contexts/ClientsDataContext";
import { useSearchParams } from "react-router-dom";

// Import new components
import OrganizationCard from "../../components/admin/clients/OrganizationCard";
import MembersSection from "../../components/admin/clients/MembersSection";
import CreateUserForm from "../../components/admin/clients/CreateUserForm";
import EditUserModal from "../../components/admin/clients/EditUserModal";
import DeleteConfirmModal from "../../components/admin/clients/DeleteConfirmModal";
import DangerZone from "../../components/admin/clients/DangerZone";
import UserCreatedModal from "../../components/admin/clients/UserCreatedModal";

const AdminClients = () => {
    const { getAccessTokenSilently } = useAuth0();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Use the ClientsDataContext for organizations, roles, and members data
    const { 
        roles, 
        organizations, 
        orgMembers, 
        isLoading: isDataLoading, 
        error: dataError, 
        refreshClientsData 
    } = useClientsData();

    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [selectedRole, setSelectedRole] = useState('');
    const [selectedOrg, setSelectedOrg] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [currentOrgId, setCurrentOrgId] = useState(null);
    const [editingUserId, setEditingUserId] = useState(null);
    const [editEmail, setEditEmail] = useState('');
    const [editName, setEditName] = useState('');
    const [editRole, setEditRole] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    // Delete confirmation state
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deletingOrg, setDeletingOrg] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    // User created modal state
    const [userCreatedModalOpen, setUserCreatedModalOpen] = useState(false);
    const [createdUserCredentials, setCreatedUserCredentials] = useState({ inviteLink: null, tempPassword: null, userName: null });

    // Tab state for organization details
    const [activeTab, setActiveTab] = useState('members'); // 'members' or 'create'

    // Animation states
    const [animationStates, setAnimationStates] = useState({
        header: false,
        clients: false,
        form: false
    });

    // Initialize state from URL parameters
    useEffect(() => {
        const orgId = searchParams.get('org');
        if (orgId && organizations.length > 0) {
            const org = organizations.find(o => o.id === orgId);
            if (org) {
                setCurrentOrgId(orgId);
                setSelectedOrg(orgId);
                setShowForm(true);
            }
        }
    }, [searchParams, organizations]);

    // Trigger staggered animations on component mount
    useEffect(() => {
        const timeouts = [];

        // Stagger the animations with delays
        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, header: true }));
        }, 100));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, clients: true }));
        }, 300));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, form: true }));
        }, 500));

        // Cleanup timeouts on unmount
        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, []);

    // Listen for page reset events from navbar
    useEffect(() => {
        const handlePageReset = (event) => {
            // Check if this page needs to reset based on its current state
            // Reset form and selection states to default
            if (showForm || currentOrgId || editingUserId || deleteConfirmOpen) {
                setShowForm(false);
                setCurrentOrgId(null);
                setEditingUserId(null);
                setIsEditModalOpen(false);
                setDeleteConfirmOpen(false);
                setDeletingOrg(null);
                setDeleteConfirmText('');
                // Reset form fields
                setEmail('');
                setFirstName('');
                setLastName('');
                setSelectedRole('');
                setSelectedOrg('');
                // Update URL
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('org');
                window.history.pushState({}, '', `?${newParams.toString()}`);
                setSearchParams(newParams);
            }
        };

        window.addEventListener('pageReset', handlePageReset);

        return () => {
            window.removeEventListener('pageReset', handlePageReset);
        };
    }, [showForm, currentOrgId, editingUserId, deleteConfirmOpen, searchParams]);

    // Initialize state from URL parameters and handle URL changes
    useEffect(() => {
        const orgId = searchParams.get('org');
        
        // Always sync state with URL
        if (!orgId) {
            setShowForm(false);
            setCurrentOrgId(null);
            setSelectedOrg('');
        } else if (organizations.length > 0) {
            const org = organizations.find(o => o.id === orgId);
            if (org) {
                setCurrentOrgId(orgId);
                setSelectedOrg(orgId);
                setShowForm(true);
            }
        }
    }, [organizations, searchParams]);

    // Listen for browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            const currentParams = new URLSearchParams(window.location.search);
            const orgId = currentParams.get('org');
            
            // Immediately sync state with URL
            if (!orgId) {
                // No org in URL, close everything
                setShowForm(false);
                setCurrentOrgId(null);
                setSelectedOrg('');
            } else if (organizations.length > 0) {
                // Try to open the org from URL
                const org = organizations.find(o => o.id === orgId);
                if (org) {
                    setCurrentOrgId(orgId);
                    setSelectedOrg(orgId);
                    setShowForm(true);
                }
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [organizations]);

    const handleOrgClick = (orgId) => {
        if (currentOrgId === orgId && showForm) {
            // Closing the form
            setSearchParams({}); // Add to history stack instead of replacing
        } else {
            // Opening a form (either new org or re-opening)
            setSearchParams({ org: orgId }); // Add to history stack instead of replacing
        }
    };

    const handleCreate = async () => {
        try {
            console.log("Attempting to create user...");
            console.log("Selected organization:", selectedOrg);
            console.log("Selected role:", selectedRole);

            const res = await createUser({
                email,
                name: `${firstName} ${lastName}`.trim() || email.split('@')[0],
                password: "TemporaryPassword123!",
                organizationId: selectedOrg,
                roleId: selectedRole
            });
            console.log("Create user response:", res);

            // Find the organization details to get the name for Zoho account matching
            const selectedOrgDetails = organizations.find(org => org.id === selectedOrg);
            const orgName = selectedOrgDetails?.display_name || selectedOrgDetails?.name;

            // Create Zoho contact if organization name exists and it's not the Helios organization
            if (orgName && orgName.toLowerCase() !== 'helios') {
                try {
                    console.log("Finding Zoho account for organization:", orgName);

                    // Find the corresponding Zoho account by name
                    const zohoAccount = await findAccountByName(orgName);

                    if (zohoAccount) {
                        console.log("Found Zoho account:", zohoAccount);

                        // Create contact data
                        const contactData = {
                            email: email,
                            firstName: firstName || email.split('@')[0],
                            lastName: lastName || 'User',
                            accountId: zohoAccount.id || zohoAccount.accountId
                        };

                        // Create the contact in Zoho Desk
                        const contactResult = await createContact(contactData);
                        console.log("Zoho contact created successfully:", contactResult);
                    } else {
                        console.warn("No matching Zoho account found for organization:", orgName);
                    }
                } catch (contactError) {
                    console.error("Failed to create Zoho contact:", contactError);
                }
            }

            // Show success modal with credentials from the API response
            setCreatedUserCredentials({
                inviteLink: res?.invite_link ?? null,
                tempPassword: res?.temp_password ?? null,
                userName: email,
            });
            setUserCreatedModalOpen(true);

            // Refresh the data using the context's refresh function
            await refreshClientsData();

            // Clear form
            setEmail('');
            setFirstName('');
            setLastName('');
            setSelectedRole('');

        } catch (error) {
            console.error("Full error object:", error);
            if (error.response) {
                console.error("Response data:", error.response.data);
                console.error("Response status:", error.response.status);
            }
            alert("Failed to create user. Check console for details.");
        }
    };

    const handleEditUser = async () => {
        try {
            if (!editingUserId || !currentOrgId) return;
            
            // Debug environment variables
            console.log('🔍 User Update Debug - Environment:', {
                REACT_APP_AUTH0_AUDIENCE: process.env.REACT_APP_AUTH0_AUDIENCE,
                REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL
            });
            
            console.log('🔍 User Update Debug - About to update user:', {
                userId: editingUserId,
                currentOrgId: currentOrgId,
                email: editEmail,
                name: editName,
                role: editRole
            });
            
            // Only update profile if email or name actually changed
            const currentUser = orgMembers[currentOrgId]?.find(member => member.user_id === editingUserId);
            const profileChanged = currentUser && (
                currentUser.email !== editEmail || 
                currentUser.name !== editName
            );
            
            if (profileChanged) {
                await updateUser(editingUserId, { email: editEmail, name: editName });
                console.log('✅ User profile updated successfully');
            }
            
            if (editRole) {
                // Use organization member role instead of global role
                await updateUserOrgRole(editingUserId, currentOrgId, editRole);
                console.log('✅ User role updated successfully');
            }
            
            alert("User updated successfully!");
            setIsEditModalOpen(false);
            // Refresh the data using the context's refresh function
            await refreshClientsData();
        } catch (error) {
            console.error("❌ Error updating user:", error);
            alert(`Failed to update user: ${error.message}`);
        }
    };

    const handleDeleteUser = async (userId) => {
        try {
            await deleteUser(userId);
            alert("User deleted successfully!");
            // Refresh the data using the context's refresh function
            await refreshClientsData();
        } catch (error) {
            console.error("Full delete error:", error.response?.data || error.message);
            alert("Failed to delete user. Check console for details.");
        }
    };

    const handleDeleteCustomer = async () => {
        if (!deletingOrg || deleteConfirmText !== deletingOrg.metadata?.customer_id && deleteConfirmText !== deletingOrg.name) {
            alert('Please type the customer ID to confirm deletion');
            return;
        }

        setIsDeleting(true);
        try {
            const token = await getAccessTokenSilently({
                audience: 'https://dev-swsatwnrscj7bqfj.us.auth0.com/api/v2/',
                scope: 'openid profile email'
            });

            const customerId = deletingOrg.metadata?.customer_id || deletingOrg.name;
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer-bootstrap/delete/${customerId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    confirmName: customerId
                })
            });

            const result = await response.json();

            if (response.ok) {
                alert('Customer deleted successfully!');
                // Refresh organizations list
                await refreshClientsData();
                setDeleteConfirmOpen(false);
                setDeletingOrg(null);
                setDeleteConfirmText('');
                setShowForm(false);
                setCurrentOrgId(null);
            } else {
                alert(`Failed to delete customer: ${result.details || result.error}`);
            }
        } catch (error) {
            console.error('Customer deletion error:', error);
            alert('Network error: Failed to delete customer');
        } finally {
            setIsDeleting(false);
        }
    };

    const openDeleteConfirm = (org) => {
        // Don't allow deleting Helios organization
        if (org.name === 'helios') {
            alert('Cannot delete the Helios organization');
            return;
        }

        setDeletingOrg(org);
        setDeleteConfirmOpen(true);
        setDeleteConfirmText('');
    };



    const handleResetPassword = async (userEmail) => {
        try {
            const token = await getAccessTokenSilently({
                audience: 'https://dev-swsatwnrscj7bqfj.us.auth0.com/api/v2/',
                scope: 'openid profile email'
            });

            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth0/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ email: userEmail })
            });

            if (response.ok) {
                alert(`Password reset email sent to ${userEmail}`);
            } else {
                const error = await response.json();
                alert(`Failed to send password reset email: ${error.message}`);
            }
        } catch (error) {
            console.error('Password reset error:', error);
            alert('Failed to send password reset email');
        }
    };



    // Show loading state while data is being fetched
    if (isDataLoading) {
        return (
            <div className="responsive-page-layout scrollable">
                <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center gap-4">
                        <Icon icon="eos-icons:loading" className="text-4xl text-primary animate-spin" />
                        <p className="text-font-secondary">Loading clients data...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Show error state if data fetching failed
    if (dataError) {
        return (
            <div className="responsive-page-layout scrollable">
                <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center gap-4 p-6 bg-red-900 bg-opacity-20 rounded-lg border border-red-500">
                        <Icon icon="mdi:alert-circle" className="text-4xl text-error" />
                        <p className="text-red-300 text-center">Failed to load clients data</p>
                        <button 
                            onClick={refreshClientsData}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="responsive-page-layout flex flex-col h-full">
            <div className={`home-title flex flex-row justify-between items-center w-full transition-all duration-700 ease-out-in ${
                animationStates.header
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 -translate-y-4'
            }`}>
                <div className="header-textbox">
                    <Icon icon="fluent:organization-16-filled" className="header-icon"></Icon>
                    Clients
                </div>
                <a className={`
                    flex flex-row gap-2 items-center text-sm p-2 button-container rounded-md
                    cursor-pointer transition-colors`}
                    target="_blank"
                    rel="noreferrer"
                    href="https://auth0.com/docs/api/management/v2"
                >
                    <Icon icon="logos:auth0-icon" className="text-lg fill-accent"></Icon>
                    <p className="font-bold text-font-secondary">Auth0 API Docs</p>
                </a>
            </div>

            {/* Breadcrumbs - Only show when expanded */}
            {showForm && (
                <div className="flex items-center gap-1 text-font-primary">
                    {/* Back button */}
                    <button
                        onClick={() => {
                            setShowForm(false);
                            setCurrentOrgId(null);
                            setSelectedOrg('');
                            setSearchParams({});
                        }}
                        className="flex items-center justify-center rounded-lg hover:bg-bgAccent transition-colors cursor-pointer"
                        title="Go back to clients"
                    >
                        <Icon icon="mdi:chevron-left" className="text-3xl text-accent hover:text-primary transition-colors" />
                    </button>
                    
                    <span 
                        className="flex items-center justify-center gap-1 group transition-colors cursor-pointer"
                        onClick={() => {
                            setShowForm(false);
                            setCurrentOrgId(null);
                            setSelectedOrg('');
                            setSearchParams({});
                        }}
                    >
                        <Icon icon="fluent:organization-16-filled" className="text-xl text-font-secondary group-hover:text-primary transition-colors" />
                        <span className="text-font-secondary group-hover:text-font-primary transition-colors">Clients</span>
                    </span>
                    <Icon icon="mdi:chevron-right" className="text-lg text-font-secondary" />
                    <span className="flex items-center justify-center gap-1">
                        <Icon icon={activeTab === 'members' ? 'mdi:account-group' : 'mdi:user-add'} className="text-2xl text-primary" />
                        <span className="text-font-primary font-semibold">
                            {activeTab === 'members' ? 'Organization Members' : 'Create User'}
                        </span>
                    </span>
                </div>
            )}
            <div className="flex-1 flex flex-col min-h-0 w-full">
                {!showForm ? (
                    /* Default Grid Layout - 4 columns */
                    <div className={` w-full gap-3 transition-all duration-1000 ease-out-in overflow-y-auto scrollable p-2 -m-2
                        ${!showForm ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4' : 'hidden'}
                        ${
                        animationStates.clients
                            ? 'opacity-100'
                            : 'opacity-0'
                    }
                    `}>
                        {organizations.map(org => (
                            <OrganizationCard
                                key={org.id}
                                org={org}
                                currentOrgId={currentOrgId}
                                showForm={showForm}
                                orgMembers={orgMembers}
                                onOrgClick={handleOrgClick}
                            />
                        ))}
                    </div>
                ) : (
                    /* Expanded Layout - Left/Right Split */
                    <div className={`w-full flex gap-4 h-full transition-all duration-1000 ease-out-in ${
                        animationStates.clients
                            ? 'opacity-100'
                            : 'opacity-0'
                    }`}>
                        {/* Organization Cards - Left Side (1/4 width) */}
                        {/* <div className={`hidden 2xl:flex flex-col w-1/4 gap-3 transition-all duration-1000 ease-out-in overflow-y-auto scrollable ${
                            animationStates.clients
                                ? 'opacity-100'
                                : 'opacity-0'
                        }`}>
                            {organizations.map(org => (
                                <OrganizationCard
                                    key={org.id}
                                    org={org}
                                    currentOrgId={currentOrgId}
                                    showForm={showForm}
                                    orgMembers={orgMembers}
                                    onOrgClick={handleOrgClick}
                                />
                            ))}
                        </div> */}

                        {/* Right Side Content (3/4 width) */}
                        <div className={`w-full flex flex-col gap-4 h-full transition-all duration-1000 ease-out-in ${
                            animationStates.form
                                ? 'opacity-100 translate-y-0'
                                : 'opacity-0 translate-y-4'
                        }`}>
                            {/* Tab Navigation */}
                            <div className="flex gap-2 w-full flex-shrink-0">
                                <button
                                    onClick={() => setActiveTab('members')}
                                    className={`tickets-tab-responsive flex flex-row gap-2 items-center justify-center p-2.5 ${
                                        activeTab === 'members'
                                            ? 'tickets-tab-active'
                                            : 'text-font-secondary border-b border-border group group-hover:text-font-primary'
                                    }`}
                                >
                                    <Icon icon="mdi:account-group" className={`text-xl ${activeTab === 'members' ? 'text-primary' : 'text-font-secondary group-hover:text-primary'}`} />
                                    <span>Organization Members</span>
                                </button>
                                <button
                                    onClick={() => setActiveTab('create')}
                                    className={`tickets-tab-responsive flex flex-row gap-2 items-center justify-center p-2.5 ${
                                        activeTab === 'create'
                                            ? 'tickets-tab-active'
                                            : 'text-font-secondary border-b border-border group group-hover:text-font-primary'
                                    }`}
                                >
                                    <Icon icon="mdi:user-add" className={`text-xl ${activeTab === 'create' ? 'text-primary' : 'text-font-secondary group-hover:text-primary'}`} />
                                    <span>Create User</span>
                                </button>
                            </div>

                            {/* Tab Content - Full width */}
                            <div className="flex-1 w-full min-h-0 overflow-y-auto scrollable">
                                {activeTab === 'members' ? (
                                    <MembersSection
                                        currentOrgId={currentOrgId}
                                        orgMembers={orgMembers}
                                        onEditUser={(member) => {
                                            setEditingUserId(member.user_id);
                                            setEditEmail(member.email);
                                            setEditName(member.name);
                                            setEditRole(member.roles?.[0]?.id || '');
                                            setIsEditModalOpen(true);
                                        }}
                                        onResetPassword={handleResetPassword}
                                        onDeleteUser={handleDeleteUser}
                                    />
                                ) : (
                                    <CreateUserForm
                                        email={email}
                                        setEmail={setEmail}
                                        firstName={firstName}
                                        setFirstName={setFirstName}
                                        lastName={lastName}
                                        setLastName={setLastName}
                                        selectedRole={selectedRole}
                                        setSelectedRole={setSelectedRole}
                                        selectedOrg={selectedOrg}
                                        organizations={organizations}
                                        roles={roles}
                                        onCreate={handleCreate}
                                    />
                                )}
                            </div>

                            <DangerZone
                                currentOrgId={currentOrgId}
                                organizations={organizations}
                                onDeleteCustomer={openDeleteConfirm}
                            />
                        </div>
                    </div>
                )}
            </div>
            <EditUserModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                editEmail={editEmail}
                setEditEmail={setEditEmail}
                editName={editName}
                setEditName={setEditName}
                editRole={editRole}
                setEditRole={setEditRole}
                roles={roles}
                onSave={handleEditUser}
            />
            <DeleteConfirmModal
                isOpen={deleteConfirmOpen}
                deletingOrg={deletingOrg}
                deleteConfirmText={deleteConfirmText}
                setDeleteConfirmText={setDeleteConfirmText}
                isDeleting={isDeleting}
                onCancel={() => {
                    setDeleteConfirmOpen(false);
                    setDeletingOrg(null);
                    setDeleteConfirmText('');
                }}
                onConfirm={handleDeleteCustomer}
            />
            <UserCreatedModal
                isOpen={userCreatedModalOpen}
                onClose={() => {
                    setUserCreatedModalOpen(false);
                    setCreatedUserCredentials({ inviteLink: null, tempPassword: null, userName: null });
                }}
                inviteLink={createdUserCredentials.inviteLink}
                tempPassword={createdUserCredentials.tempPassword}
                userName={createdUserCredentials.userName}
            />
        </div>
    );
};

export default AdminClients;